import type { ResolvedEvidence, ScalarFieldSpec } from "../types.js";
import { changeReasonName, projectDisplayValue, toneFor, type DisplayRow, type MarkedLine, type PartyLine } from "../schema/formModel.js";
import { DisplayRowList } from "./ContentBlocks.js";
import { fieldErrorId } from "./FieldRow.js";
import "./styles.css";

/** The label above the reason box. */
export const CHANGE_REASON_PROMPT = "Why are you changing this answer?";

/** The control that puts the file's own answer back and drops the obligation with it. */
export const REVERT_LABEL = "Use the answer from the file";

/**
 * What the file said, for the assessor to read WHILE they explain disagreeing with it. The
 * source chip is gone by the time this shows (it is a claim about the current answer, and the
 * current answer is no longer the file's), so this line is what keeps the thing being overridden
 * on screen instead of only in the audit row.
 */
export function fileAnswerLine(answerLabel: string, sourceLabel?: string | null): string {
  const source = sourceLabel?.trim();
  return source ? `${source} says: ${answerLabel}` : `The answer on file: ${answerLabel}`;
}

export interface FromCtxPanelProps {
  spec: ScalarFieldSpec;
  /**
   * The served pre-answer as the OPTION LABEL the assessor reads, or null when the file could
   * not answer. Projected by the caller through the schema's own options list — this component
   * holds no schema and never turns a wire value into copy.
   */
  preAnswerLabel: string | null;
  /** Has the answer moved away from the pre-answer? Decided by `formModel.changedFromPreAnswer`. */
  changed: boolean;
  reason: string;
  error?: string;
  onReasonChange: (value: string) => void;
  onRevert: () => void;
}

/**
 * Phase 532 — what renders UNDER a question the job's own ctx has something to say about
 * (ARD-DSL-051).
 *
 * <h3>Three states, one component</h3>
 * <ul>
 *   <li><b>Pre-answered.</b> The control already shows the file's answer selected and the
 *   source chip names where it came from (both done by the renderer's EXISTING channels —
 *   `preAnswerPatch` writes the answer, `prefilled` writes the chip). This panel adds the
 *   evidence rows: the actual values behind the claim, so the assessor can agree with a fact
 *   rather than with a label.</li>
 *   <li><b>Not pre-answered.</b> The server resolved no answer and said why — a screen that did
 *   not complete, an outcome the declaration has no mapping for, a path with nothing on it yet.
 *   That reason renders as the PROMPT TO ANSWER MANUALLY beside an unanswered question. It is
 *   never a selected option: "we could not tell" and "the file says no" are different sentences
 *   and only one of them is on the file.</li>
 *   <li><b>Changed.</b> The reason box, required, plus the file's own answer restated and a
 *   one-click way back to it.</li>
 * </ul>
 *
 * <h3>Why the control is not locked behind a Change button</h3>
 * <p>The pre-answer arrives as an ORDINARY ANSWER in an ordinary choice control, so the change
 * affordance is the control itself: the other option is one click away, exactly as it is on
 * every other question of the form. A lock would buy a confirmation step and cost the property
 * that makes this safe to ship — that a pre-answered field and a typed one are the same kind of
 * thing to validation, autosave, the review step and the payload. The mis-click a lock would
 * catch is caught here instead, by {@link REVERT_LABEL}, without a second interaction model.</p>
 *
 * <h3>Two declarations, one panel</h3>
 * <p>The served keys it reads are written by two server-side producers — a field's own `from_ctx`
 * block (`FormCtxPreAnswerResolver`) and a bound approval gate's `computed_from` on the decision
 * step the engine generates (`ApprovalFormBinding`). Both arrive in the same keys, so both land
 * here. What differs is the CHANGE arm: only the `from_ctx` convention writes its reason to
 * `<name>_change_reason`, so only a field that owns that convention is ever handed
 * `changed: true` (`formModel.ownsChangeReason`). A gate's reason is a declared field of its own,
 * revealed by the predicate the engine serves beside it — the same interaction, not a second one.</p>
 *
 * <p>Generic by construction: it names no field, no form and no source. Every string it renders
 * is one the declaration wrote or the server resolved.</p>
 */
export function FromCtxPanel({
  spec,
  preAnswerLabel,
  changed,
  reason,
  error,
  onReasonChange,
  onRevert,
}: FromCtxPanelProps) {
  const title = spec.from_ctx_evidence_title?.trim() ?? "";
  const rows = evidenceRows(spec.evidence, title.length > 0);
  const unresolved = spec.from_ctx_unresolved_reason?.trim() ?? "";
  // Nothing on the file, nothing said about why, nothing changed — render nothing rather than an
  // empty box under every question of a form that merely COULD carry a pre-answer.
  if (rows.length === 0 && unresolved.length === 0 && !changed) return null;

  const reasonId = changeReasonName(spec.name);
  const errorId = fieldErrorId(reasonId);

  return (
    <div className="from-ctx">
      {title.length > 0 && rows.length > 0 && <p className="from-ctx-evidence-title">{title}</p>}
      <DisplayRowList rows={rows} className="from-ctx-evidence" />
      {unresolved.length > 0 && !changed && (
        <p className="from-ctx-unresolved" role="status">{unresolved}</p>
      )}
      {changed && (
        <div className="from-ctx-change">
          {preAnswerLabel !== null && (
            <p className="from-ctx-was">{fileAnswerLine(preAnswerLabel, spec.from_ctx_source_label)}</p>
          )}
          <label className="field-label" htmlFor={reasonId}>{CHANGE_REASON_PROMPT}</label>
          <textarea
            id={reasonId}
            name={reasonId}
            required
            className="field-input"
            aria-describedby={error ? errorId : undefined}
            aria-invalid={error ? true : undefined}
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
          />
          {error && <p id={errorId} className="field-error" role="status">{error}</p>}
          <button type="button" className="from-ctx-revert" onClick={onRevert}>{REVERT_LABEL}</button>
        </div>
      )}
    </div>
  );
}

/**
 * The evidence rows worth showing: the declared caption, and the value the SERVER resolved put
 * through the module's ONE projector ({@link projectDisplayValue}) — the very call a step card's
 * rows make through `displayRows`.
 *
 * <p>Phase 559 — <b>one projector, because there is one grammar.</b> A display row has ONE closed
 * key set (`FORM_FROM_CTX_EVIDENCE_KEYS`, packages/workflow-schema) and ONE deploy-time rule
 * holding it to what it declares (ARD-DSL-062), so a second projection here would be two readings
 * of one declaration — the defect this phase exists to remove. The only thing this site does that
 * the card site does not is RESOLVE NOTHING: the path was walked server-side and its value is
 * served beside the caption, along with the row's projection keys copied verbatim
 * ({@link ResolvedEvidence}). The renderer still walks no path.</p>
 *
 * <p><b>The one deliberate difference between the two sites</b> is what an unshowable value reads
 * as, and it is a difference in the READER's situation, not in the projection. Both arms get the
 * same "nothing to show" answer from the projector (a value of `length === 0`). A step card is a
 * LEDGER the assessor is reading as the record — a caption there with nothing after it has to say
 * `formModel.NOT_RECORDED`, because "the file does not record this" is itself a fact they must
 * weigh (`formModel.displayRows`). Evidence under a question is a PROMPT beside a control — an empty
 * caption there is noise between the question and the answer, so the row is dropped. Same
 * projection, same emptiness contract, two honest presentations of it.</p>
 */
function evidenceRows(evidence: ResolvedEvidence[] | undefined, titled = false): DisplayRow[] {
  const rows: DisplayRow[] = [];
  for (const row of evidence ?? []) {
    if (typeof row?.label !== "string" || row.label.trim().length === 0) continue;
    // Walk 2 — the engine's GENERATED code list (a `from_ctx.table` field): the described codes
    // as a list, the ones matching this question marked, and the served legend under it. It is
    // served already in words, so it bypasses the declared-row projector.
    const marked = markedLines(row.value);
    if (marked !== null) {
      if (marked.length === 0) continue;
      const legend = typeof row.legend === "string" ? row.legend.trim() : "";
      rows.push(legend.length > 0 ? { label: row.label, value: marked, note: legend } : { label: row.label, value: marked });
      continue;
    }
    const value = projectDisplayValue(row, row.value);
    if (value.length === 0) continue;
    // Walk 2 — a TITLED box is one results table: each party becomes its own line, captioned by
    // the party's name, and the box's title supersedes the row's own caption. Untitled ⇒ the
    // nested per-party list under the row's caption, exactly as before.
    if (titled && isPartyLineList(value)) {
      for (const line of value) rows.push({ label: line.party, value: line.value });
      continue;
    }
    const tone = toneFor(row, row.value);
    rows.push(tone === undefined ? { label: row.label, value } : { label: row.label, value, tone });
  }
  return rows;
}

/**
 * The served `[{text, marked}]` list, or null when the value is not that shape. An item without
 * readable text is dropped; `marked` is true only when the server said so.
 */
function markedLines(value: unknown): MarkedLine[] | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  if (!value.every((item) => typeof item === "object" && item !== null && "text" in item && "marked" in item)) return null;
  const lines: MarkedLine[] = [];
  for (const item of value as Array<{ text: unknown; marked: unknown }>) {
    if (typeof item.text !== "string" || item.text.trim().length === 0) continue;
    lines.push({ text: item.text, marked: item.marked === true });
  }
  return lines;
}

function isPartyLineList(value: DisplayRow["value"]): value is PartyLine[] {
  return Array.isArray(value) && value.length > 0
    && typeof value[0] === "object" && value[0] !== null && "party" in value[0];
}
